import type { DecisionTemplateInput } from "./types.js";

/**
 * Render a new Decision-Log entry body using an obsidian-style template
 * convention. Caller is responsible for writing the result to disk via
 * {@link DecisionStore.pathFor}.
 *
 * The output deliberately mirrors a human-authored template so the
 * shape stays consistent whether the entry was created by hand or by
 * this helper.
 */
export function renderTemplate(input: DecisionTemplateInput): string {
  const tags = ["decision-log", ...(input.tags ?? [])];
  const tagsLine = tags.map((t) => `"${t}"`).join(", ");
  const area = input.area ?? "work / personal / other";

  return [
    "---",
    "type: decision-log",
    "status: active",
    "privacy: personal",
    `created: ${input.date}`,
    `updated: ${input.date}`,
    `tags: [${tagsLine}]`,
    // Typed cross-references the ontos graph reads deterministically. Each entry is a
    // `<relation>: <target>` map; target is a typed id (`type:slug`) or a bare slug when
    // the relation's type is unambiguous. e.g.  - builds-on: decision:<slug>
    //                                           - applies: rule:<memory-slug>
    "relations: []",
    "---",
    "",
    `# ${input.title}`,
    "",
    `- **Date**: ${input.date}`,
    `- **Area**: ${area}`,
    "- **Tag**: #decision-log",
    "",
    "## Context",
    "",
    "{What problem prompted this decision? Why was a choice required? 2-3 sentences.}",
    "",
    "## Options",
    "",
    "| Option | Content | Pros | Cons |",
    "|--------|---------|------|------|",
    "| A | | | |",
    "| B | | | |",
    "| C | | | |",
    "",
    "## Chosen: {A/B/C}",
    "",
    "## Why this one",
    "",
    "{The core section. The real reasoning behind the choice, candidly.}",
    "",
    "## Principle (patterns emerge when this repeats)",
    "",
    "> {One sentence: the judgment criterion this decision exposed.}",
    "",
    "## Result (optional)",
    "",
    "{Fill in only for verifiable decisions. Delete the section for taste/preference records.}",
    "",
  ].join("\n");
}
